import React, { useRef, useState, useLayoutEffect } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Cell,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Component, ChartConfig } from "@/types";
import { getChartSizeConfig, getChartResponsiveConfig } from "@/utils/chart-responsive-utils";
import { useQueryTemplateData, isQueryTemplateDataSource, getXAxisKeyFromQuery, getLineKeysFromQuery } from "@/hooks/useQueryTemplateData";
import { ChartDataSource } from "@/types/chartDataSource";
import { resolveColorArray } from "@/utils/cssVariableResolver";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useChartNavigation } from "@/hooks/useChartNavigation";
import { useComponentInteraction } from "@/components/shared/rendering/InteractionContextRuntime";
import { NavigationIndicator } from "./NavigationIndicator";
import { ChartLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";

interface HeatmapChartProps {
  component: Component;
  isPreview?: boolean;
}

const defaultData = [
  { x: 'Mon', y: 'Morning', value: 20 },
  { x: 'Mon', y: 'Afternoon', value: 35 },
  { x: 'Mon', y: 'Evening', value: 25 },
  { x: 'Tue', y: 'Morning', value: 25 },
  { x: 'Tue', y: 'Afternoon', value: 40 },
  { x: 'Tue', y: 'Evening', value: 30 },
  { x: 'Wed', y: 'Morning', value: 30 },
  { x: 'Wed', y: 'Afternoon', value: 45 },
  { x: 'Wed', y: 'Evening', value: 35 },
  { x: 'Thu', y: 'Morning', value: 28 },
  { x: 'Thu', y: 'Afternoon', value: 42 },
  { x: 'Thu', y: 'Evening', value: 32 },
  { x: 'Fri', y: 'Morning', value: 22 },
  { x: 'Fri', y: 'Afternoon', value: 38 },
  { x: 'Fri', y: 'Evening', value: 28 },
];

const defaultColors = [
  "#fee5d9",
  "#fcbba1",
  "#fc9272",
  "#fb6a4a",
  "#ef3b2c",
  "#cb181d",
  "#99000d",
];

type ChartRow = Record<string, unknown>;

type HeatmapDatum = ChartRow & {
  xIndex: number;
  yIndex: number;
  originalX: string;
  originalY: string;
  _isTotal?: boolean;
};

type TooltipPayloadEntry = {
  payload?: Partial<HeatmapDatum>;
};

type LegendFormatterValue = {
  name?: unknown;
  key?: unknown;
};

type HeatmapShapeProps = {
  cx?: number;
  cy?: number;
  fill?: string;
  payload?: HeatmapDatum;
};

type HeatmapClickPayload = {
  payload?: Partial<HeatmapDatum>;
  index?: number | string;
};

function toOptionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

const getNumericCellValue = (row: ChartRow, key: string): number => {
  return toOptionalNumber(row[key]) ?? 0;
};

const formatLegendValue = (value: unknown): React.ReactNode => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  if (typeof value === 'object' && value !== null) {
    const legendValue = value as LegendFormatterValue;
    const displayValue = legendValue.name ?? legendValue.key;
    if (typeof displayValue === 'string' || typeof displayValue === 'number') {
      return displayValue;
    }
  }

  return String(value);
};

// Helper to get color based on value
const getColorForValue = (value: number, min: number, max: number, colors: string[]): string => {
  if (max === min) return colors[0];
  const normalized = (value - min) / (max - min);
  const index = Math.floor(normalized * (colors.length - 1));
  return colors[Math.min(index, colors.length - 1)];
};

// Format cell label using template with {value} token
const formatCellLabel = (template: string, value: number): string => {
  return template.replace('{value}', String(value));
};

// Auto-contrast text color based on WCAG luminance
const getContrastColor = (hexColor: string): string => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

// Aggregation types for row/column totals
type AggregationType = 'sum' | 'average' | 'max' | 'min';

const aggregate = (values: number[], type: AggregationType): number => {
  if (values.length === 0) return 0;
  switch (type) {
    case 'sum': return values.reduce((a, b) => a + b, 0);
    case 'average': return values.reduce((a, b) => a + b, 0) / values.length;
    case 'max': return Math.max(...values);
    case 'min': return Math.min(...values);
    default: return values.reduce((a, b) => a + b, 0);
  }
};

export const HeatmapChart: React.FC<HeatmapChartProps> = ({
  component,
  isPreview = false,
}) => {
  const { properties = {}, style = {}, size } = component;
  const containerRef = useRef<HTMLDivElement>(null);

  // Track palette changes via CSS variable mutations
  const [paletteChangeCounter, setPaletteChangeCounter] = useState(0);

  // Get dataSource configuration
  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const templateId = properties.queryTemplateId as string | undefined;
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);

  // Execute Query Template if configured
  const {
    data: queryTemplateData,
    isLoading: qtLoading,
    error: qtError,
    refetch: qtRefetch,
    lastFetched,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate || !!templateId,
    templateId,
    initialData: properties.data as Record<string, unknown>[] | undefined,
  });

  // Loading/error state from query template
  const loading = qtLoading;
  const error = qtError;

  // Data priority: Query template > Static data > Default data
  const chartData = queryTemplateData
    || (properties.data && Array.isArray(properties.data)
      ? properties.data
      : defaultData);

  // Determine axis keys - use query definition if available, validate against data, or auto-detect
  const queryXKey = hasQueryTemplate && dataSource?.queryDefinition ? getXAxisKeyFromQuery(dataSource.queryDefinition) : null;
  const autoXKey = chartData.length > 0
    ? Object.keys(chartData[0]).find(k => typeof chartData[0][k] === 'string' || k.includes('date') || k.includes('time'))
    : null;
  const xKey = (properties.xAxisKey as string)
    || (queryXKey && chartData.length > 0 && queryXKey in chartData[0] ? queryXKey : null)
    || autoXKey
    || queryXKey
    || "x";

  // For yKey, check if groupBy has a second dimension
  const groupByFields = hasQueryTemplate && dataSource?.queryDefinition?.groupBy
    ? dataSource.queryDefinition.groupBy.map((g) => g.field.split('.').pop() || g.field)
    : [];

  const yKey = (properties.yAxisKey as string)
    || (groupByFields.length > 1 ? groupByFields[1] : null)
    || "y";

  // valueKey is the first (usually only) measure — validate against actual data
  const queryValueKeys = hasQueryTemplate && dataSource?.queryDefinition ? getLineKeysFromQuery(dataSource.queryDefinition) : null;
  const validatedValueKey = queryValueKeys && queryValueKeys[0] && chartData.length > 0 && queryValueKeys[0] in chartData[0]
    ? queryValueKeys[0] : null;
  const autoValueKey = chartData.length > 0
    ? Object.keys(chartData[0]).find(key => key !== xKey && key !== yKey && typeof chartData[0][key] === 'number')
    : null;
  const explicitValueKey = typeof properties.valueKey === 'string' ? properties.valueKey : null;
  const fallbackLineKey = Array.isArray(properties.lineKeys)
    ? properties.lineKeys.find((key): key is string => typeof key === 'string')
    : null;
  const valueKey = explicitValueKey
    || fallbackLineKey
    || validatedValueKey
    || autoValueKey
    || (queryValueKeys?.[0])
    || "value";

  // Cell label properties
  const showCellLabels = properties.showCellLabels === true;
  const cellLabelFormat = (properties.cellLabelFormat as string) || '{value}';
  const cellLabelColor = properties.cellLabelColor as string | undefined;
  const cellFontSize = (properties.cellFontSize as number | 'auto') || 'auto';

  // Row/column total properties
  const showRowTotals = properties.showRowTotals === true;
  const showColumnTotals = properties.showColumnTotals === true;
  const totalAggregation = (properties.totalAggregation as AggregationType) || 'sum';
  const rowTotalLabel = (properties.rowTotalLabel as string) || 'Total';
  const columnTotalLabel = (properties.columnTotalLabel as string) || 'Total';

  // Get size configuration from component.size (no ResizeObserver needed)
  const sizeConfig = getChartSizeConfig(
    toOptionalNumber(size?.width),
    toOptionalNumber(size?.height)
  );
  // Get responsive configuration
  const title = (properties.title as string | undefined) || "Heatmap Chart";
  const description = properties.description as string;
  const userMargin = properties.margin as ChartConfig['margin'];

  const responsiveConfig = getChartResponsiveConfig(
    sizeConfig,
    !!title,
    !!description,
    userMargin,
    properties.showLegend !== false,
    properties.showGrid !== false
  );

  // Resolve CSS variables in colors to actual color values for Recharts
  const [resolvedColors, setResolvedColors] = useState<string[]>(() => {
    const colors = (properties.colors as string[]) || defaultColors;
    return colors;
  });

  // Watch for CSS variable changes on ancestor with data-palette-scope
  useLayoutEffect(() => {
    const findPaletteScope = (element: HTMLElement | null): HTMLElement | null => {
      while (element) {
        if (element.dataset.paletteScope === 'true') return element;
        element = element.parentElement;
      }
      return null;
    };

    const paletteScope = findPaletteScope(containerRef.current);
    if (!paletteScope) return;

    const observer = new MutationObserver(() => {
      setPaletteChangeCounter(c => c + 1);
    });

    observer.observe(paletteScope, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, []);

  // Resolve colors after DOM is mounted to ensure palette scope CSS variables are accessible
  // Re-resolve when palette changes to update CSS variable values
  useLayoutEffect(() => {
    const colors = (properties.colors as string[]) || defaultColors;
    const resolved = resolveColorArray(colors, containerRef.current);
    setResolvedColors(resolved);
  }, [properties.colors, paletteChangeCounter]);

  const chartConfig: ChartConfig = {
    title,
    data: chartData,
    xAxisKey: xKey,
    yAxisKey: yKey,
    showLegend: responsiveConfig.showLegend,
    showTooltip: properties.showTooltip !== false,
    showGrid: responsiveConfig.showGrid,
    colors: resolvedColors,
    responsive: properties.responsive !== false,
    animation: properties.animation !== false,
    margin: responsiveConfig.margin,
  };

  // Chart navigation hooks for click-through navigation
  const interaction = useComponentInteraction(component);
  const { handleChartClick, isNavigable, getNavigationForElement } = useChartNavigation(component, interaction);

  // Calculate min/max values for color scaling using original data (excluding totals)
  const values = chartData.map((d: ChartRow) => getNumericCellValue(d, valueKey));
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  // Get unique x and y values for domain (preserving order from data)
  const xValuesSet = new Set<string>();
  const yValuesSet = new Set<string>();
  chartData.forEach((d: ChartRow) => {
    xValuesSet.add(String(d[chartConfig.xAxisKey] ?? ''));
    yValuesSet.add(String(d[chartConfig.yAxisKey] ?? ''));
  });
  const xValues = Array.from(xValuesSet);
  const yValues = Array.from(yValuesSet);

  // Compute augmented values with totals if enabled
  const augmentedXValues = showRowTotals ? [...xValues, rowTotalLabel] : xValues;
  const augmentedYValues = showColumnTotals ? [...yValues, columnTotalLabel] : yValues;

  let augmentedData: ChartRow[] = [...chartData];

  if (showRowTotals) {
    yValues.forEach(yVal => {
      const rowValues = chartData
        .filter((d: ChartRow) => String(d[yKey] ?? '') === yVal)
        .map((d: ChartRow) => getNumericCellValue(d, valueKey));
      augmentedData.push({
        [xKey]: rowTotalLabel,
        [yKey]: yVal,
        [valueKey]: aggregate(rowValues, totalAggregation),
        _isTotal: true,
      });
    });
  }

  if (showColumnTotals) {
    xValues.forEach(xVal => {
      const colValues = chartData
        .filter((d: ChartRow) => String(d[xKey] ?? '') === xVal)
        .map((d: ChartRow) => getNumericCellValue(d, valueKey));
      augmentedData.push({
        [xKey]: xVal,
        [yKey]: columnTotalLabel,
        [valueKey]: aggregate(colValues, totalAggregation),
        _isTotal: true,
      });
    });
  }

  // Corner cell (total of totals)
  if (showRowTotals && showColumnTotals) {
    const allValues = chartData.map((d: ChartRow) => getNumericCellValue(d, valueKey));
    augmentedData.push({
      [xKey]: rowTotalLabel,
      [yKey]: columnTotalLabel,
      [valueKey]: aggregate(allValues, totalAggregation),
      _isTotal: true,
    });
  }

  // Transform categorical data to numeric indices for proper heatmap display
  const transformedData: HeatmapDatum[] = augmentedData.map((d: ChartRow) => ({
    ...d,
    xIndex: augmentedXValues.indexOf(String(d[xKey] ?? '')),
    yIndex: augmentedYValues.indexOf(String(d[yKey] ?? '')),
    originalX: String(d[xKey] ?? ''),
    originalY: String(d[yKey] ?? ''),
  }));

  // Get zAxisRange from properties or use default
  const zAxisRange = (properties.zAxisRange as [number, number]) || [400, 1000];

  // Calculate minimum height for the chart area
  const dynamicMinimum = sizeConfig.isVerySmall ? 60 : sizeConfig.isSmall ? 100 : 120;

  // Container style - let it fill the wrapper
  // Strip width-related properties from component.style - sizing is handled by ComponentWrapper/FlexContainer
  const { width: _w, minWidth: _mw, maxWidth: _xw, ...visualStyle } = style as React.CSSProperties;
  const containerStyle: React.CSSProperties = {
    ...visualStyle,
    position: 'relative',
    height: '100%',
    width: '100%',
    minHeight: (typeof properties.minHeight === 'string' || typeof properties.minHeight === 'number')
      ? properties.minHeight
      : `${dynamicMinimum + (responsiveConfig.showTitle ? 40 : 0)}px`,
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div className="w-full h-full" style={containerStyle} ref={containerRef}>
      <NavigationIndicator isNavigable={isNavigable && !isPreview} />
      {responsiveConfig.showTitle && (
        <div className={`text-center ${responsiveConfig.titleMargin} relative`}>
          <h3 className={responsiveConfig.titleClass}>{chartConfig.title}</h3>
          {responsiveConfig.showDescription && (
            <p className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
          {/* Refresh button for query template data sources */}
          {hasQueryTemplate && !isPreview && (
            <button
              onClick={() => qtRefetch()}
              className="absolute right-2 top-0 p-1 rounded hover:bg-muted/50 transition-colors"
              title={lastFetched ? `Last updated: ${lastFetched.toLocaleTimeString()}` : 'Refresh data'}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      )}

      <div style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
        {loading ? (
          <ChartLoadingSkeleton variant="heatmap" />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : chartData && chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={chartConfig.margin}
              style={{ userSelect: isPreview ? "auto" : "none" }}
            >
              {chartConfig.showGrid && (
                <CartesianGrid strokeDasharray="3 3" />
              )}
              <XAxis
                type="number"
                dataKey="xIndex"
                name={chartConfig.xAxisKey}
                tick={{ fontSize: responsiveConfig.axisFontSize }}
                tickFormatter={(index) => augmentedXValues[index] || ''}
                domain={[-0.5, augmentedXValues.length - 0.5]}
                ticks={Array.from({ length: augmentedXValues.length }, (_, i) => i)}
              />
              <YAxis
                type="number"
                dataKey="yIndex"
                name={chartConfig.yAxisKey}
                tick={{ fontSize: responsiveConfig.axisFontSize }}
                tickFormatter={(index) => augmentedYValues[index] || ''}
                domain={[-0.5, augmentedYValues.length - 0.5]}
                ticks={Array.from({ length: augmentedYValues.length }, (_, i) => i)}
              />
              <ZAxis
                type="number"
                dataKey={valueKey}
                range={zAxisRange}
                name={valueKey}
              />
              {chartConfig.showTooltip && (
                <Tooltip
                  cursor={{ strokeDasharray: "3 3" }}
                  contentStyle={{
                    backgroundColor: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "6px",
                  }}
                  formatter={(value: unknown, name: unknown): [React.ReactNode, string] => {
                    const label = String(name);
                    if (label === valueKey) return [String(value), valueKey];
                    return [String(value), label];
                  }}
                  labelFormatter={(label: unknown, payload?: TooltipPayloadEntry[]) => {
                    const tooltipPayload = payload?.[0]?.payload;
                    if (tooltipPayload) {
                      return `${tooltipPayload.originalX ?? ''} - ${tooltipPayload.originalY ?? ''}`;
                    }
                    return String(label);
                  }}
                />
              )}
              {chartConfig.showLegend && (
              <Legend
                wrapperStyle={{ fontSize: `${responsiveConfig.legendFontSize}px` }}
                  formatter={formatLegendValue}
                />
              )}
              <Scatter
                data={transformedData}
                animationDuration={chartConfig.animation ? 300 : 0}
                shape={(shapeProps: HeatmapShapeProps) => {
                  const { cx, cy, fill, payload } = shapeProps;
                  if (!payload || cx === undefined || cy === undefined) {
                    return <g />;
                  }
                  const cellFill = fill ?? defaultColors[0];
                  const isTotal = payload._isTotal === true;
                  const cellValue = getNumericCellValue(payload, valueKey);
                  const labelText = showCellLabels
                    ? formatCellLabel(cellLabelFormat, cellValue)
                    : null;
                  const textColor = cellLabelColor || getContrastColor(cellFill);
                  // Scale radius proportional to value, clamped to [minR, maxR]
                  const minR = 14;
                  const maxR = 20;
                  const normalized = maxValue === minValue
                    ? 0.5
                    : Math.min(1, Math.max(0, (cellValue - minValue) / (maxValue - minValue)));
                  const r = minR + normalized * (maxR - minR);
                  const computedFontSize = cellFontSize === 'auto' ? Math.max(9, Math.min(12, r * 0.55)) : cellFontSize;
                  return (
                    <g>
                      <circle cx={cx} cy={cy} r={r} fill={cellFill} stroke="none" />
                      {labelText && (
                        <text
                          x={cx}
                          y={cy}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill={textColor}
                          fontSize={computedFontSize}
                          fontWeight={isTotal ? 'bold' : 'normal'}
                        >
                          {labelText}
                        </text>
                      )}
                    </g>
                  );
                }}
                onClick={(data: HeatmapClickPayload) => {
                  // Use original values for navigation lookup
                  const payload = data.payload ?? {};
                  const originalPayload = {
                    ...payload,
                    [xKey]: payload.originalX,
                    [yKey]: payload.originalY
                  };
                  handleChartClick(originalPayload, valueKey, toOptionalNumber(data.index));
                }}
                cursor={isNavigable ? 'pointer' : 'default'}
              >
                {transformedData.map((entry: HeatmapDatum, index: number) => {
                  const cellColor = getColorForValue(
                    getNumericCellValue(entry, valueKey),
                    minValue,
                    maxValue,
                    chartConfig.colors || defaultColors
                  );
                  const cellNav = getNavigationForElement(valueKey, entry.originalX);
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={cellColor}
                      cursor={isNavigable && cellNav ? 'pointer' : 'default'}
                    />
                  );
                })}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">No chart data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
